import {
  calcShingleBundles, calcRidgeCapBundles, calcIceWaterRolls, calcUnderlaymentRolls,
  calcStarterBundles, calcCoilNailBoxes, calcCapNailBoxes, type RoofM,
} from "./formulas";
import { getPackages, type ManufacturerKey, type ManufacturerPackage, type PackageLineItem } from "./packages";

export interface RetailMeasurements {
  totalSquares: number;
  eaveLF?: number; rakeLF?: number; ridgeLF?: number; hipLF?: number; valleyLF?: number;
  pipeCount?: number; facetCount?: number; stories?: number; pitch?: string;
}

export interface TierItem {
  item: string; description: string; category: string;
  qty: number; unit: string; unitPrice: number; total: number;
}

export interface TierResult {
  items: TierItem[]; total: number;
  manufacturer: string; systemName: string; warrantyName: string; highlights: string[];
}

function fillMeasurements(m: RetailMeasurements): RoofM {
  const sq = Math.max(1, m.totalSquares);
  const estPerim = Math.round(Math.sqrt(sq * 100) * 4);
  return {
    totalSquares: sq,
    eaveLF: m.eaveLF || Math.round(estPerim * 0.45),
    rakeLF: m.rakeLF || Math.round(estPerim * 0.35),
    ridgeLF: m.ridgeLF || Math.round(estPerim * 0.25),
    hipLF: m.hipLF || Math.round(estPerim * 0.10),
    valleyLF: m.valleyLF || Math.round(estPerim * 0.15),
    pipeCount: m.pipeCount || 4,
    facetCount: m.facetCount || 4,
    stories: m.stories || 1,
    pitch: m.pitch || "6/12",
  };
}

function resolveQty(formula: string, m: RoofM): number {
  if (formula.startsWith("ridge_bdl_")) {
    const lf = Number(formula.split("_")[2]) || 25;
    return calcRidgeCapBundles(m.ridgeLF, m.hipLF, lf);
  }
  switch (formula) {
    case "sq": return m.totalSquares;
    case "eave_lf": return m.eaveLF;
    case "rake_lf": return m.rakeLF;
    case "ridge_lf": return m.ridgeLF;
    case "valley_lf": return m.valleyLF;
    case "pipe_count": return m.pipeCount || 4;
    case "step_flash": return (m.facetCount || 4) * 8;
    case "starter_bdl": return calcStarterBundles(m.eaveLF, m.rakeLF);
    case "underlayment_rl": return calcUnderlaymentRolls(m.totalSquares);
    case "iw_rl": return calcIceWaterRolls(m);
    case "nail_boxes": return calcCoilNailBoxes(m.totalSquares);
    case "cap_nail_boxes": return calcCapNailBoxes(m.totalSquares);
    case "dumpster": return Math.max(1, Math.ceil(m.totalSquares / 30));
    case "fixed_1": return 1;
    default: return 0;
  }
}

function shouldInclude(li: PackageLineItem, m: RoofM): boolean {
  if (!li.conditional) return true;
  if (li.conditionKey === "steep") {
    const rise = parseInt((m.pitch || "6/12").match(/(\d+)\/12/)?.[1] || "6");
    return rise >= 7;
  }
  return false;
}

function buildItems(pkg: ManufacturerPackage, m: RoofM): TierItem[] {
  const items: TierItem[] = [];
  const process = (lis: PackageLineItem[]) => {
    for (const li of lis) {
      if (!shouldInclude(li, m)) continue;
      const qty = Math.round(resolveQty(li.qtyFormula, m) * 100) / 100;
      if (qty <= 0) continue;
      items.push({
        item: li.item, description: li.description, category: li.category,
        qty, unit: li.unit, unitPrice: li.midPrice,
        total: Math.round(qty * li.midPrice * 100) / 100,
      });
    }
  };
  process(pkg.materials);
  process(pkg.labor);
  return items;
}

export function generateTiers(manufacturer: ManufacturerKey, measurements: RetailMeasurements) {
  const pkgs = getPackages(manufacturer);
  const m = fillMeasurements(measurements);
  const build = (key: "good" | "better" | "best"): TierResult => {
    const pkg = pkgs[key];
    const items = buildItems(pkg, m);
    const total = Math.round(items.reduce((s, i) => s + i.total, 0) * 100) / 100;
    return {
      items, total,
      manufacturer: pkg.manufacturerLabel,
      systemName: pkg.systemName,
      warrantyName: pkg.warrantyName,
      highlights: pkg.highlights,
    };
  };
  return { good: build("good"), better: build("better"), best: build("best") };
}
